import { randomUUID } from '@betterment/core';
import { useGraphQLSuspenseQuery } from '@betterment/graphql';
import { useAutoNavigate } from '@betterment/js-runtime';
import { Banner, Box, ContentLayoutF, H2, Text } from '@betterment/samba-react';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { formatLegalAccountType } from '@betterment/shared-components/src/utils/StringHelpers';

import { FeeAttempt } from './data';
import { BulkFeeAttemptDocument, BulkFeeAttemptQuery } from './graphql/query.bulkFeeAttempt.generated';
import { useSubmitHandler } from './hooks/useSubmitHandler';
import ReviewFeesTable from './review-fees-table/ReviewFeesTable';
import ReviewSummaryBox from './review-summary-box/ReviewSummaryBox';

import urlBuilder from '../../../Shared/url-builder';

type BulkFeeAttempt = NonNullable<BulkFeeAttemptQuery['firm']['bulkFeeAttempt']>;

const transformFeeAttempts = (bulkFeeAttempt: BulkFeeAttempt): FeeAttempt[] => {
  if (bulkFeeAttempt.feeAttempts.__typename !== 'FeeAttemptBatch') {
    return [];
  }

  return bulkFeeAttempt.feeAttempts.attempts.map(attempt => ({
    id: randomUUID(),
    owner: attempt.account.legalAccount.owner,
    accountName: attempt.account.name,
    legalAccountType: formatLegalAccountType(attempt.account.legalAccount.type),
    amount: attempt.amount.cents,
    description: attempt.reason ?? '',
  }));
};

export const OnDemandBillingReview = () => {
  const id = useRequiredParam('id');
  const navigate = useAutoNavigate();
  const { submitHandler, errorMessage, isSubmitting } = useSubmitHandler();

  const { data } = useGraphQLSuspenseQuery(BulkFeeAttemptDocument, {
    variables: { id },
  });

  const { bulkFeeAttempt } = data.firm;

  if (!bulkFeeAttempt) {
    void navigate(urlBuilder.getOnDemandBillingStartPage.absolutePath);
    return null;
  }

  const feeAttempts = transformFeeAttempts(bulkFeeAttempt);

  return (
    <Box css={{ width: 'container.max', display: 'flex', flexDirection: 'column', justifySelf: 'center' }}>
      <H2 css={{ pb: 'space.400' }}>Review {bulkFeeAttempt.name}</H2>
      {errorMessage && (
        <Banner variant="critical" css={{ mt: 'space.400' }}>
          <Banner.Content>{errorMessage}</Banner.Content>
        </Banner>
      )}
      <ContentLayoutF>
        <ContentLayoutF.SlotContainer>
          <ContentLayoutF.Slot1>
            <ReviewFeesTable feeAttempts={feeAttempts} />
          </ContentLayoutF.Slot1>
          <ContentLayoutF.Slot2>
            <Box css={{ position: 'sticky', top: '20px' }}>
              <ReviewSummaryBox
                feeAttempts={feeAttempts}
                bulkFeeAttemptId={bulkFeeAttempt.id}
                bulkFeeAttemptName={bulkFeeAttempt.name}
                onSubmit={submitHandler}
                isSubmitting={isSubmitting}
              />
              <Box css={{ mt: 'space.300' }}>
                <Text color="text.weak" variant="text50">
                  You are responsible for communicating these fees to your clients, and for ensuring the amount charged
                  is fair and reasonable.
                </Text>
              </Box>
            </Box>
          </ContentLayoutF.Slot2>
        </ContentLayoutF.SlotContainer>
      </ContentLayoutF>
    </Box>
  );
};
